import {skipToken} from '@reduxjs/toolkit/query';
import {useLazyGetQuickProductsQuery} from 'components/customer/Product/store/productApi';
import {useLazyGetProductDataQuery} from 'components/customer/Product/store/useProductApi';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {getPlaceholderDisplay} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';
import {useJobContext} from 'contexts/JobContext';
import {getProductParams} from 'hooks/preCacheProducts';
import {useCallback, useEffect, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {PartialJob, Job} from 'shared/types/PartialJob';

export const useProductName = () => {
    const [name, setName] = useState('');
    const [productNumber, setProductNumber] = useState('');
    const [searchParams] = useSearchParams();
    const {roomId} = useParams();
    const [cabinetNote, setCabinetNote] = useState<string | null>(null);
    const {selectedCabinetGroup} = useRoomPlannerContext();

    const productId = searchParams.get('product');
    const cabinetId = searchParams.get('cabinetId');
    const placeholderEdit = searchParams.get('placeholderEdit');
    const placeholderFace = searchParams.get('placeholderFace');
    const placeholderName = searchParams.get('placeholderName');

    const {job, room} = useJobContext() as PartialJob;

    const [getProductData] = useLazyGetProductDataQuery();
    const [getProducts] = useLazyGetQuickProductsQuery();

    const getProductName = useCallback(
        async (productId: string) => {
            try {
                const params = getProductParams({room});
                if (params === skipToken) return;

                const {data: products} = await getProducts(params);

                if (products) {
                    const product = products.find(
                        (product) => product.id == Number(productId)
                    );

                    if (product) {
                        const productName =
                            product.changed_name ?? product.text;
                        setName(productName);
                    }
                }
            } catch (e) {}
        },
        [room]
    );

    const getProductNumber = useCallback(
        async (
            productId: string,
            cabinetId: string,
            roomId: string,
            job: Job
        ) => {
            const {data: productData} = await getProductData(
                {
                    cabinetId: Number(cabinetId),
                    roomId: Number(roomId),
                    cabinetType: Number(productId),
                },
                true
            );

            if (productData) {
                const roomCabinetNumber = productData.room_cab_number;

                const room = job?.rooms?.find(
                    (room) => room.id == Number(roomId)
                );

                if (room) {
                    setProductNumber(`${room.roomNumber}-${roomCabinetNumber}`);
                }

                // if product name is updated via notes let's update it here since we're holding productData already
                setCabinetNote(
                    !!productData.cabinet_note ? productData.cabinet_note : null
                );
            }
        },
        []
    );

    useEffect(() => {
        if (placeholderEdit || placeholderFace) {
            const {name: meshName} =
                getPlaceholderDisplay(selectedCabinetGroup);
            setName(placeholderName || meshName || 'Appliance');
            return;
        }
        if (productId) void getProductName(productId);
        else setName('');
        // cabinetId in this effect deps allows switching between identical cabinets with different cabinet_notes to reflect their modified name
    }, [
        productId,
        cabinetId,
        placeholderEdit,
        placeholderFace,
        placeholderName,
        selectedCabinetGroup,
    ]);

    useEffect(() => {
        if (productId && cabinetId && roomId && job) {
            void getProductNumber(productId, cabinetId, roomId, job);
        } else {
            setProductNumber('');
            setCabinetNote(null);
        }
    }, [cabinetId, productId, roomId, job]);

    useEffect(() => {
        // effect to update the name if cabinet note is modified
        if (cabinetNote && cabinetNote !== name) {
            setName(cabinetNote);
        }
    }, [name, cabinetNote]);

    return {
        name,
        productNumber,
    };
};
